/**
 * Drag-and-drop state architecture
 *
 * @dnd-kit uses a single shared DragDropProvider, so all draggable items must
 * live in one flat data structure. The server shape (nested Plan) is flattened
 * into three pieces of local state:
 *
 *   stepsByGroup  Record<string, ContentStep[]>
 *     Keys are 'top-level' (for steps outside any section) and each section's
 *     id. Built from the server plan via buildStepsByGroup(). This is the
 *     source of truth for what renders during and after a drag — section
 *     metadata (name, id) still comes from localPlan, but steps always come
 *     from here.
 *
 *   sectionOrder  string[]
 *     The ordered list of section ids. Kept separate so move() can reorder it
 *     as a flat array during section drags without touching stepsByGroup.
 *
 *   localPlan  Plan
 *     A copy of the server plan used for non-reorderable metadata (section
 *     names, step content). Written back to after each drop via persistState
 *     so it stays consistent until React Query delivers the next server update.
 *
 * Drag lifecycle:
 *
 *   onDragStart — snapshot current state into snapshotRef for cancel recovery.
 *
 *   onDragOver  — fires on every hover. For step drags only, calls move() on
 *     stepsByGroup to produce an optimistic reorder. The UI updates live so
 *     the item follows the cursor instantly.
 *
 *   onDragEnd   — two cases:
 *     - Canceled: restore stepsByGroup and sectionOrder from snapshotRef.
 *     - Section drag: call move() on sectionOrder to get the final order, then
 *       persistState with the updated order.
 *     - Step drag: onDragOver has already applied all moves optimistically;
 *       just call persistState to commit what is already on screen.
 *
 *   persistState(sbg, secOrder) — always called with refs (not state) to avoid
 *     stale closure issues. Reconstructs the full Plan content shape and writes
 *     it to both localPlan (for immediate render consistency) and the server
 *     via updateContent.
 *
 * Server sync:
 *
 *   When React Query delivers a new planInput reference (i.e. the server
 *   responded after a save), the prevPlanInput !== planInput check resets all
 *   local state from the server. This only fires when not dragging so it never
 *   snaps items back mid-drag.
 *
 * Drag handle (handleRef) flow:
 *
 *   @dnd-kit supports restricting drags to a specific handle element via the
 *   `handle` option on useSortable. It expects a ref pointing to the handle
 *   DOM node. Only pointer events on that node will initiate a drag.
 *
 *   The ref is created inside each sortable wrapper (SortableSection,
 *   SortableTopStep, SortableSectionStep) and passed down via a render-prop
 *   children pattern:
 *
 *     children: (handleRef: React.Ref<HTMLElement>) => React.ReactNode
 *
 *   The wrapper renders its draggable container, registers the ref with
 *   useSortable, then calls children(handleRef) to let the consumer place the
 *   ref wherever it wants. This keeps the drag registration logic in the
 *   wrapper while the consuming component decides which element is the handle.
 *
 *   For steps: handleRef lands on <SixDotIcon ref={handleRef} /> in
 *   PlanStepContainer — only the grip icon initiates a drag.
 *
 *   For sections: handleRef is passed through Section → Title, where it lands
 *   on the wrapper <div> around the section's SixDotIcon — same effect.
 */
import { useLayoutEffect, useRef, useState } from 'react';

import { move } from '@dnd-kit/helpers';
import { DragDropProvider, DragEndEvent, DragOverEvent } from '@dnd-kit/react';
import { isSortable, useSortable } from '@dnd-kit/react/sortable';
import { useAtomValue } from 'jotai';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { ContentStep, Plan, PlanSection } from '@cfa/pathway-fe-shared/xp';
import { Surface } from '@cfa/react-core';

import useAuth from '@/hooks/useAuth';
import { useUpdatePlan } from '@/hooks/useUpdatePlan';

import ConfirmationModal from '@/components/popups/ConfirmationModal';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import Constants from '@/constants';

import { TaskProps } from '@/types/types';

import PlanStepContainer, { SectionFunctionProps } from './PlanStepContainer';

import { userPrefsAtom } from '@/atoms/userPrefs';
import Section from '@/routes/TrainingPlans/BuildPlans/EditablePlanItems/Section';

interface PlanItemsProps {
  plan: Plan;
  addProcedure: (id?: string) => void;
  addQuiz: (quiz: string) => void;
  setIsDrop: (isDrop: boolean) => void;
  editProcedureResource?: ({
    stepId,
    sectionId,
  }: {
    stepId: string;
    sectionId?: string;
  }) => void;
  isReadOnly?: boolean;
}

// ─── Sortable wrapper components ────────────────────────────────────────────

interface SortableSectionProps {
  section: PlanSection;
  index: number;
  isReadOnly: boolean;
  children: (handleRef: React.Ref<HTMLElement>) => React.ReactNode;
}

const SortableSection: React.FC<SortableSectionProps> = ({
  section,
  index,
  isReadOnly,
  children,
}) => {
  const [element, setElement] = useState<Element | null>(null);
  const handleRef = useRef<HTMLElement | null>(null);
  useSortable({
    id: section.id,
    index,
    type: 'section',
    // Accept steps with low collision priority so individual step items
    // (normal priority) win when hovering over populated sections, but the
    // section itself is still a valid drop target for empty sections.
    accept: ['step', 'section'],
    collisionPriority: 1, // CollisionPriority.Low
    disabled: isReadOnly,
    element,
    handle: handleRef,
  });

  return (
    <div ref={setElement} className="my-6 first:mt-0 last:mb-0">
      {children(handleRef)}
    </div>
  );
};

interface SortableTopStepProps {
  step: ContentStep;
  index: number;
  isReadOnly: boolean;
  children: (handleRef: React.Ref<HTMLElement>) => React.ReactNode;
}

const SortableTopStep: React.FC<SortableTopStepProps> = ({
  step,
  index,
  isReadOnly,
  children,
}) => {
  const [element, setElement] = useState<Element | null>(null);
  const handleRef = useRef<HTMLElement | null>(null);
  useSortable({
    id: step.id,
    index,
    type: 'step',
    accept: 'step',
    group: 'top-level',
    disabled: isReadOnly,
    element,
    handle: handleRef,
  });

  return (
    <div ref={setElement} className="my-6 first:mt-0 last:mb-0">
      {children(handleRef)}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────

const buildStepsByGroup = (plan: Plan): Record<string, ContentStep[]> => ({
  'top-level': plan.content?.steps ?? [],
  ...Object.fromEntries(
    (plan.content?.sections ?? []).map(s => [s.id, s.steps ?? []]),
  ),
});

const PlanItems: React.FC<PlanItemsProps> = ({
  plan: planInput,
  addProcedure,
  addQuiz,
  setIsDrop,
  editProcedureResource,
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  const { language: userLanguage } = useAtomValue(userPrefsAtom);
  const { updateContent, deleteStep, deleteSection, renameSection, isPending } =
    useUpdatePlan();
  const [editMode, setEditMode] = useState(false);
  const [sectionBeingRenamed, setSectionBeingRenamed] = useState({
    id: '',
    name: '',
  });
  const [taskToDelete, setTaskToDelete] = useState<{
    id: string;
    name: string;
    sectionId: string | null;
    type: string;
  }>({
    id: '',
    name: '',
    sectionId: '',
    type: '',
  });
  const [showDeleteTaskPopup, setShowDeleteTaskPopup] = useState(false);
  const [showDeleteSectionPopup, setShowDeleteSectionPopup] = useState(false);
  const [localPlan, setLocalPlan] = useState(planInput);
  const [taskUpdated, setTaskUpdated] = useState(false);

  const [isDragging, setIsDragging] = useState(false);

  const [stepsByGroup, setStepsByGroup] = useState<
    Record<string, ContentStep[]>
  >(() => buildStepsByGroup(planInput));
  const [sectionOrder, setSectionOrder] = useState<string[]>(() =>
    (planInput.content?.sections ?? []).map(s => s.id),
  );

  // Keep refs so event handlers always read the latest values.
  const stepsByGroupRef = useRef(stepsByGroup);
  const sectionOrderRef = useRef(sectionOrder);
  const isDraggingRef = useRef(isDragging);
  useLayoutEffect(() => {
    stepsByGroupRef.current = stepsByGroup;
    sectionOrderRef.current = sectionOrder;
    isDraggingRef.current = isDragging;
  });

  const snapshotRef = useRef<{
    stepsByGroup: Record<string, ContentStep[]>;
    sectionOrder: string[];
  } | null>(null);

  // When React Query delivers a new planInput (e.g. after a save), reset all
  // local drag state from the server. The render-time setState pattern is
  // React's documented recommendation for "adjusting state when a prop changes"
  // (react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes).
  // A useEffect would cause cascading renders and is flagged by React Compiler.
  const [prevPlanInput, setPrevPlanInput] = useState(planInput);
  if (!isDragging && prevPlanInput !== planInput) {
    setPrevPlanInput(planInput);
    setLocalPlan(planInput);
    setStepsByGroup(buildStepsByGroup(planInput));
    setSectionOrder((planInput.content?.sections ?? []).map(s => s.id));
    setIsDrop(false);
  }

  const { data: auth } = useAuth();
  const locationsWithAtLeastLeader =
    auth?.allLocationsWithAtLeastLeaderRoles ?? [];

  const onDeleteSectionCancel = () => {
    setSectionBeingRenamed({ id: '', name: '' });
    setShowDeleteSectionPopup(false);
  };

  const onDeleteSection = async (section: { id: string; name: string }) => {
    setSectionBeingRenamed({ id: '', name: '' });
    setShowDeleteSectionPopup(false);
    await deleteSection(planInput, section.id);
    toast.custom(toastObj => (
      <ToastMessageBlock id={toastObj.id}>
        {`${section.name} ${t('TrainingPlans.toastMessage.sectionDeleted')}`}
      </ToastMessageBlock>
    ));
  };

  const deleteSectionPopUp = (sectionId: string, sectionName: string) => {
    setSectionBeingRenamed({ id: sectionId, name: sectionName });
    setShowDeleteSectionPopup(true);
  };

  const isAllowedToEdit = () => locationsWithAtLeastLeader.length > 0;

  const renameSectionPopUp = (sectionId: string) => {
    setEditMode(true);
    setSectionBeingRenamed({ id: sectionId, name: '' });
  };

  const updateSectionName = (updatedSectionTitle: string) => {
    if (!updatedSectionTitle.trim()) return;
    const existingName = localPlan.content?.sections?.find(
      s => s.id === sectionBeingRenamed.id,
    )?.name;
    renameSection(planInput, sectionBeingRenamed.id, {
      ...existingName,
      [userLanguage]: updatedSectionTitle,
    });
  };

  const updateTask = (task: TaskProps, sectionId: string) => {
    const makeUpdatedStep = (step: ContentStep): ContentStep => {
      // `note` and `urls` only exist on task steps.
      if (step.id !== task.id || step.type !== Constants.STEP_TYPES.TASK) {
        return step;
      }
      return {
        ...step,
        name: { ...step.name, [userLanguage]: task.name },
        note: { ...step.note, [userLanguage]: task.note },
        urls: [
          {
            urlSet: {
              ...step.urls?.[0]?.urlSet,
              [userLanguage]: task.url,
            },
          },
        ],
      };
    };

    const groupKey = sectionId || 'top-level';
    const newStepsByGroup = {
      ...stepsByGroupRef.current,
      [groupKey]: (stepsByGroupRef.current[groupKey] ?? []).map(
        makeUpdatedStep,
      ),
    };
    setStepsByGroup(newStepsByGroup);

    const newSections = sectionOrderRef.current.map(id => {
      const meta = localPlan.content?.sections?.find(s => s.id === id);
      return {
        ...(meta ?? { id, name: {}, steps: [] }),
        steps: newStepsByGroup[id] ?? [],
      };
    });
    updateContent(planInput, {
      steps: newStepsByGroup['top-level'] ?? [],
      sections: newSections,
    });
    setTaskUpdated(true);
  };

  const onUpdate = (
    type: string,
    updatedSectionTitle: string | null,
    task: TaskProps,
    sectionId: string,
  ) => {
    if (
      type === Constants.TRAINING_MENU_OPTIONS.SECTION &&
      updatedSectionTitle
    ) {
      updateSectionName(updatedSectionTitle);
    } else {
      updateTask(task, sectionId);
    }
    setEditMode(false);
    setSectionBeingRenamed({ id: '', name: '' });
  };

  const editSectionTitle = (section: PlanSection) =>
    isAllowedToEdit() && editMode && sectionBeingRenamed.id === section.id;

  const onDeleteTaskInsideSection = ({
    id,
    name,
    sectionId,
    type,
  }: SectionFunctionProps) => {
    setTaskToDelete({ id, name, sectionId, type });
    setShowDeleteTaskPopup(true);
  };

  const onDeleteTask = async () => {
    await deleteStep(planInput, taskToDelete.id, taskToDelete.sectionId);
    toast.custom(toastObj => (
      <ToastMessageBlock id={toastObj.id}>
        {`${taskToDelete.name} ${t('TrainingPlans.toastMessage.deleted')}`}
      </ToastMessageBlock>
    ));
    setShowDeleteTaskPopup(false);
  };

  const onCancel = () => {
    setEditMode(false);
  };

  const onDeleteTaskOutsideSection = ({
    id,
    name,
    type,
  }: {
    id: string;
    name: string;
    type: string;
  }) => {
    setTaskToDelete({ id, name, sectionId: null, type });
    setShowDeleteTaskPopup(true);
  };

  const persistState = (
    sbg: Record<string, ContentStep[]>,
    secOrder: string[],
  ) => {
    const newSections = secOrder.map(sectionId => {
      const meta = localPlan.content?.sections?.find(s => s.id === sectionId);
      return {
        ...(meta ?? { id: sectionId, name: {}, steps: [] }),
        steps: sbg[sectionId] ?? [],
      };
    });
    const newContent = {
      steps: sbg['top-level'] ?? [],
      sections: newSections,
    };
    setLocalPlan(prev => ({
      ...prev,
      content: { ...prev.content, ...newContent },
    }));
    updateContent(planInput, newContent);
  };

  const onDragStart = () => {
    snapshotRef.current = {
      stepsByGroup: stepsByGroupRef.current,
      sectionOrder: sectionOrderRef.current,
    };
    setIsDragging(true);
    setIsDrop(true);
  };

  const onDragOver = (event: DragOverEvent) => {
    const { source } = event.operation;
    if (source?.type !== 'step') return;
    setStepsByGroup(current => move(current, event));
  };

  const onDragEnd = (event: DragEndEvent) => {
    setIsDragging(false);

    if (event.canceled) {
      if (snapshotRef.current) {
        setStepsByGroup(snapshotRef.current.stepsByGroup);
        setSectionOrder(snapshotRef.current.sectionOrder);
      }
      setIsDrop(false);
      return;
    }

    const { source } = event.operation;
    if (!isSortable(source)) return;

    if (source.type === 'section') {
      const newOrder = move(sectionOrderRef.current, event);
      setSectionOrder(newOrder);
      persistState(stepsByGroupRef.current, newOrder);
      return;
    }

    if (source.type === 'step') {
      // Apply a final move to guarantee the ref is current, then persist.
      const final = move(stepsByGroupRef.current, event);
      setStepsByGroup(final);
      persistState(final, sectionOrderRef.current);
    }
  };

  const groupOrder = ['top-level', ...sectionOrder];

  return (
    <DragDropProvider
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
    >
      <div className="w-full">
        {groupOrder.map((groupId, outerIdx) => {
          if (groupId === 'top-level') {
            return (stepsByGroup['top-level'] ?? []).map(
              (step: ContentStep, idx: number) => (
                <SortableTopStep
                  key={`${step.id}-plan-step`}
                  step={step}
                  index={idx}
                  isReadOnly={isReadOnly}
                >
                  {handleRef => (
                    <Surface className="pathway-surface">
                      <PlanStepContainer
                        handleRef={handleRef}
                        isAllowedToEdit={isAllowedToEdit()}
                        language={userLanguage}
                        onCancel={onCancel}
                        onDelete={onDeleteTaskOutsideSection}
                        onEdit={(type, updatedSectionTitle, task, sectionId) =>
                          onUpdate(type, updatedSectionTitle, task, sectionId)
                        }
                        step={step}
                        taskUpdated={taskUpdated}
                        editProcedureResource={editProcedureResource}
                        isReadOnly={isReadOnly}
                      />
                    </Surface>
                  )}
                </SortableTopStep>
              ),
            );
          }

          const section = localPlan.content?.sections?.find(
            s => s.id === groupId,
          );
          if (!section) return null;
          const sectionIdx = outerIdx - 1;
          return (
            <SortableSection
              key={`${groupId}-plan-sections`}
              section={section}
              index={sectionIdx}
              isReadOnly={isReadOnly}
            >
              {handleRef => (
                <Section
                  addProcedure={() => section.id && addProcedure(section.id)}
                  editProcedureResource={editProcedureResource}
                  addQuiz={() => section.id && addQuiz(section.id)}
                  deleteSectionPopUp={deleteSectionPopUp}
                  editSectionTitle={editSectionTitle}
                  handleRef={handleRef}
                  index={sectionIdx}
                  isAllowedToEdit={isAllowedToEdit()}
                  language={userLanguage}
                  onCancel={onCancel}
                  onClickThreeDotMenuDeleteTask={onDeleteTaskInsideSection}
                  onEdit={(
                    type: string,
                    updatedSectionTitle: string | null,
                    task: TaskProps,
                    id: string,
                  ) => onUpdate(type, updatedSectionTitle, task, id)}
                  plan={planInput}
                  renameSectionPopUp={renameSectionPopUp}
                  section={{ ...section, steps: stepsByGroup[groupId] ?? [] }}
                  setActiveSectionId={id => id}
                  taskUpdated={taskUpdated}
                  isReadOnly={isReadOnly}
                />
              )}
            </SortableSection>
          );
        })}
      </div>
      <ConfirmationModal
        bodyText={t('Generic.deleteObject', {
          name: sectionBeingRenamed.name,
          object: t('Generic.section').toLowerCase(),
        })}
        headerText={t('Generic.deleteHeader', {
          type: t('Generic.section'),
        })}
        isDisabled={isPending}
        isLoading={isPending}
        isOpen={showDeleteSectionPopup}
        onClose={onDeleteSectionCancel}
        primaryButtonHandler={() => onDeleteSection(sectionBeingRenamed)}
        primaryButtonText={t('Button.delete')}
        primaryButtonVariant="destructive"
        secondaryButtonHandler={onDeleteSectionCancel}
        secondaryButtonText={t('Button.cancel')}
      />
      <ConfirmationModal
        bodyText={
          taskToDelete.type === Constants.STEP_TYPES.TASK
            ? t('Generic.deleteObject', {
                name: taskToDelete.name,
                object: t('Generic.task').toLowerCase(),
              })
            : t('Generic.removeObject', {
                name: taskToDelete.name,
                object:
                  taskToDelete.type === Constants.STEP_TYPES.DOCUMENT
                    ? t('Generic.resource').toLowerCase()
                    : t('Generic.quiz').toLowerCase(),
              })
        }
        headerText={
          taskToDelete.type === Constants.STEP_TYPES.TASK
            ? t('Generic.deleteHeader', {
                type: t('Generic.task'),
              })
            : t('Generic.removeHeader', {
                type:
                  taskToDelete.type === Constants.STEP_TYPES.DOCUMENT
                    ? t('Generic.resource')
                    : taskToDelete.type === Constants.STEP_TYPES.QUIZ
                      ? t('Generic.quiz')
                      : t('Generic.task'),
              })
        }
        isDisabled={isPending}
        isLoading={isPending}
        isOpen={showDeleteTaskPopup}
        onClose={() => {
          setShowDeleteTaskPopup(false);
        }}
        primaryButtonHandler={onDeleteTask}
        primaryButtonText={
          taskToDelete.type === Constants.STEP_TYPES.TASK
            ? t('Button.delete')
            : t('Button.remove')
        }
        primaryButtonVariant="destructive"
        secondaryButtonHandler={() => {
          setShowDeleteTaskPopup(false);
        }}
        secondaryButtonText={t('Button.cancel')}
      />
    </DragDropProvider>
  );
};

export default PlanItems;
